import React from 'react';
import classNames from 'classnames';
import {withI18n, WithI18nProps} from '@shopify/react-i18n';
import {Shop} from 'interfaces';
import {Card, Text, Flex} from 'components/UIKit';
import {
  CardChart,
  CardCta,
  CardHeader,
  CardMetrics,
  CardScreenshot,
  CardMetricTypes,
} from './components';
import * as styles from './ListingCard.scss';
import * as locales from './locales';

interface ListingCardProps {
  shop: Shop;
  group?: string;
  loading?: boolean;
  showImage: boolean;
  layout: string;
  metricsToShow?: CardMetricTypes[];
}

export interface ListingCardState {
  chartData: {[date: string]: string};
  isMobile: boolean;
}

export function ListingCardComponent({
  shop,
  group,
  loading,
  showImage,
  i18n,
  layout,
  metricsToShow,
}: ListingCardProps & WithI18nProps) {
  const shopsPath = i18n.translate('shopsPath', {path: shop.handle});
  const chartData = (shop.shopifyShopRecord.monthByMonthRevenue || []).reduce((memo, month) => {
    memo[month.date] = Math.max(0, month.revenue);
    return memo;
  }, {});

  const handleCardClick = () => (location.href = shopsPath);

  const ListingCardImage = () => (
    <div className={classNames(styles.Visual, styles[layout])}>
      {showImage ? (
        <CardScreenshot
          shop={shop}
          alt={i18n.translate('screenshotAlt', {shopTitle: shop.title})}
        />
      ) : (
        <CardChart
          id={group + shop.title}
          data={chartData}
          title={i18n.translate('totalRevenue')}
          total={shop.shopifyShopRecord.totalRevenue}
          loading={loading}
          layout={layout}
          chartOffsets={(layout === 'grid' && 16) || 0}
        />
      )}
    </div>
  );

  const ListingCardContent = () => (
    <>
      <CardHeader shop={shop} shopPath={shopsPath} layout={layout} />
      <CardMetrics shop={shop} layout={layout} metricsToShow={metricsToShow} />
      <div className={classNames(styles.pitch, styles[layout])}>
        <Text
          text={shop.marketplaceDescription || i18n.translate('pitchPlaceholder')}
          secondary={!shop.marketplaceDescription}
          resetMargin
          component="p"
          type="small"
        />
      </div>
      <CardCta shop={shop} shopPath={shopsPath} layout={layout} />
    </>
  );

  const GridContainer = () => (
    <>
      <Flex className={classNames(styles.Content)} style={{padding: 0}}>
        <ListingCardImage />
        <Flex className={styles.Content}>
          <ListingCardContent />
        </Flex>
      </Flex>
    </>
  );

  const ListContainer = () => (
    <>
      <ListingCardImage />
      <ListingCardContent />
    </>
  );

  return (
    <Card
      contentClassName={classNames(styles.listingCard, styles[layout], {
        [styles.loading]: loading,
      })}
      component="li"
      onClick={handleCardClick}
    >
      {layout === 'grid' ? <GridContainer /> : <ListContainer />}
    </Card>
  );
}

export const ListingCard = withI18n({
  id: 'ListingCard',
  fallback: locales.en,
})(ListingCardComponent);
